import Box from "@mui/material/Box";
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { styles } from "./styles";
import { formStyles } from "./addUserStyles";
import Loader from "components/loader";
import SHOW_TOAST from "utils/showToast";
import { getUserListing } from "./action";
import ROUTE_NAMES from "routes/routeNames";
import { editUserSchema } from "utils/schema";
import { useNavigate } from "react-router-dom";
import UserDopdown from "components/userDropdown";
import React, { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_USER_REDUCER } from "store/actionTypes";
import { UPLOAD_FILE_TO_S3 } from "utils/services";
import {
  ACCEPTED_IMAGE_ACCEPT,
  ACCEPTED_IMAGE_TYPES,
  FOLDER,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_MB,
} from "utils/constants";
import VehiclesDropdown from "components/vehiclesDropdown";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CarCompaniesDropdown from "components/carCompaniesDropdown";
import DocumentPreviewModal from "components/documentPreviewModal";
import { UpdateUser, getUserDetailFromEdit } from "./action";
import { APIResponseModal, ReducersModal, UserDetailModal } from "utils/modals";
import { USER_ROLE, USER_TYPE, getUserRoleLabel } from "utils/userType";
import { toDateInputValue } from "utils/apiMappers";
import { isLocalPreviewUrl } from "utils/commonFunctions";
import { addVichleToUser, delelteVichleToUser } from "modules/company/action";

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={formStyles.section}>
    <Typography component="h2" sx={formStyles.sectionTitle}>
      {title}
    </Typography>
    <Box sx={formStyles.grid}>{children}</Box>
  </Box>
);

export default function EditUser() {
  const { loader, result } = useSelector(
    (state: ReducersModal) => state.UserReducer
  );

  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const [docLink, setDocLink] = useState("");
  const [docUploading, setDocUploading] = useState(false);
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [profilePhotoChanged, setProfilePhotoChanged] = useState(false);
  const [userData, setUserData] = useState<any>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoadingState, setLoadingState] = useState(false);
  const selectedProfileFileRef = React.useRef<File | null>(null);

  const [userVechilesList, setUserVechleList] = React.useState<any[]>([]);
  const [vehiclePickerKey, setVehiclePickerKey] = React.useState(0);
  const UserId =
    new URLSearchParams(window.location.search).get("id") ?? undefined;

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const getMyVichles = () => {
    setLoadingState(true);
    dispatch(
      getUserDetailFromEdit((response: any) => {
        const vehicles = Array.isArray(response?.data?.vehicle)
          ? response.data.vehicle
          : [];
        if (response?.isSuccess) {
          setUserData(response.data);
        }
        setUserVechleList(vehicles);
        setLoadingState(false);
      }, UserId)
    );
  };

  const startDocumentUpload = async (file: File, setFieldValue: Function) => {
    setDocUploading(true);
    try {
      const documentUrl = await UPLOAD_FILE_TO_S3(file, FOLDER.USER_DOCUMENT);
      if (!documentUrl) {
        SHOW_TOAST("Unable to upload document");
        return;
      }
      setDocLink(documentUrl);
      setFieldValue("document", documentUrl);
    } catch (err: any) {
      SHOW_TOAST(err?.message || "Unable to upload document");
    } finally {
      setDocUploading(false);
    }
  };
  const onImagePicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function
  ) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    const acceptedByType = ACCEPTED_IMAGE_TYPES.includes(file.type);
    const acceptedByName = /\.(jpe?g|png|webp|avif)$/i.test(file.name);
    if (!acceptedByType && !acceptedByName) {
      SHOW_TOAST("Please upload a JPG, PNG, WEBP, or AVIF image");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      SHOW_TOAST(`Please upload an image smaller than ${MAX_IMAGE_SIZE_MB} MB`);
      return;
    }
    const localPreview = URL.createObjectURL(file);
    if (isLocalPreviewUrl(imageUrl)) URL.revokeObjectURL(imageUrl);
    selectedProfileFileRef.current = file;
    setProfilePhotoChanged(true);
    setImageUrl(localPreview);
    setFieldValue("profilePhoto", localPreview);
  };
  const onDocumentPicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function
  ) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    startDocumentUpload(file, setFieldValue);
  };
  const fileRef = React.useRef<HTMLInputElement>(null);
  const docRef = React.useRef<HTMLInputElement>(null);

  const onUploadImageClick = () => {
    fileRef.current?.click();
  };

  const onUploadDocumentClick = () => {
    docRef.current?.click();
  };

  const dispatchValue = (payload: any) => {
    dispatch({
      type: UPDATE_USER_REDUCER,
      payload,
    });
  };

  useEffect(() => {
    if (UserId) {
      dispatchValue({ UserId: UserId });
      setLoading(true);
      dispatch(
        getUserDetailFromEdit((response: any) => {
          if (response?.isSuccess) {
            setUserData(response?.data);
            const vehicles = Array.isArray(response.data?.vehicle)
              ? response.data.vehicle
              : [];
            setUserVechleList(vehicles);
            selectedProfileFileRef.current = null;
            setProfilePhotoChanged(false);
            const profilePicture = response?.data?.profilePhoto || "";
            setImageUrl(profilePicture);
            setDocLink(response?.data?.document || "");
          }
          setLoading(false);
        }, UserId)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserId]);

  const initialValues = {
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || "",
    email: userData?.email || "",
    phoneNumber: userData?.phoneNumber || "",
    profilePhoto: userData?.profilePhoto || "",
    // Leave blank to keep the current password; never prefill from API.
    password: "",
    confirmPassword: "",
    addressLine1: userData?.addressLine1 || "",
    addressLine2: userData?.addressLine2 || "",
    city: userData?.city || "",
    state: userData?.state || "",
    country: userData?.country || "India",
    pincode: userData?.pincode || "",
    gstNumber: userData?.gstNumber || "",
    document: userData?.document || "",
    salary: userData?.salary || 0,
    dateOfJoining: toDateInputValue(userData?.dateOfJoining),
    registrationNumber: userData?.registrationNumber || "",
    franchiseAmount: userData?.franchiseAmount || "",
    numberOfStaff: userData?.numberOfStaff || 0,
    role: userData?.role || "",
    coordinates: userData?.coordinates || [],
    vendorId: userData?.vendorId || "",
    vehicleBrandId: "",
    companyName: "",
    vehicleId: "",
    vehicleType: "",
  };

  const handleOnSubmit = async (values: any) => {
    setLoading(true);
    try {
      let profilePhoto = values.profilePhoto || "";
      if (selectedProfileFileRef.current) {
        profilePhoto = await UPLOAD_FILE_TO_S3(
          selectedProfileFileRef.current,
          FOLDER.PROFILE
        );
        if (!profilePhoto) {
          SHOW_TOAST("Unable to upload profile photo");
          setLoading(false);
          return;
        }
      }
      const dataToSend = {
        ...values,
        id: UserId,
        profilePhoto,
        document: values.document || docLink,
      };
      dispatch(
        UpdateUser(dataToSend, async (resp: APIResponseModal) => {
          if (resp.isSuccess) {
            selectedProfileFileRef.current = null;
            navigate(ROUTE_NAMES.MAIN_ROUTES.USER_MANAGEMENT.BASE);
          }
          setLoading(false);
        })
      );
    } catch (err: any) {
      setLoading(false);
    }
  };

  const formatNumber = (phNumber: string) => {
    let input = `${phNumber || ""}`.replace(/\D/g, "");
    return input;
  };

  const getVendorList = () => {
    dispatchValue({
      limit: 100,
      page: 1,
      result: [],
      userType: USER_TYPE.MERCHANT,
    });
    dispatch(getUserListing());
  };

  useEffect(() => {
    // Service partners are linked to a franchise, so the franchise list is needed
    if (userData?.role === USER_ROLE.SERVICE_PARTNER) getVendorList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const renderView = () => {
    if (loading) {
      return <></>;
    }
    return (
      <Formik
        onSubmit={handleOnSubmit}
        validationSchema={editUserSchema}
        initialValues={initialValues}
        validateOnMount
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          setFieldTouched,
          validateField,
          dirty,
          isSubmitting,
        }) => (
          <Box
            component="form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (profilePhotoChanged || selectedProfileFileRef.current) {
                handleOnSubmit(values);
                return;
              }
              handleSubmit(event);
            }}
            sx={formStyles.card}
          >
            <Box sx={formStyles.photoRow}>
              <Box
                component="button"
                type="button"
                onClick={onUploadImageClick}
                sx={formStyles.photoButton}
                aria-label="Change profile photo"
              >
                {imageUrl || values.profilePhoto ? (
                  <Box
                    component="img"
                    alt=""
                    src={imageUrl || values.profilePhoto}
                    sx={formStyles.photo}
                  />
                ) : (
                  <Box sx={formStyles.photoEmpty}>
                    <PhotoCameraOutlinedIcon />
                  </Box>
                )}
              </Box>
              <Box>
                <Typography sx={formStyles.photoLabel}>Profile photo</Typography>
                <Button
                  type="button"
                  onClick={onUploadImageClick}
                  sx={formStyles.textAction}
                >
                  Change photo
                </Button>
              </Box>
              <input
                id="fileInput"
                type="file"
                ref={fileRef}
                accept={ACCEPTED_IMAGE_ACCEPT}
                style={{ display: "none" }}
                onChange={(e) => onImagePicked(e, setFieldValue)}
              />
            </Box>
            <FormSection title="Account">
                <FormControl fullWidth>
                  <InputLabel>{"User Type"}</InputLabel>
                  <Select
                    disabled
                    label="User Type"
                    name="role"
                    native={false}
                    variant="outlined"
                    value={values.role}
                    onChange={(e: any) => {
                      setFieldValue("role", e.target.value);
                    }}
                  >
                    <MenuItem value={USER_ROLE.COORDINATOR}>
                      {getUserRoleLabel(USER_ROLE.COORDINATOR)}
                    </MenuItem>
                    <MenuItem value={USER_ROLE.MERCHANT}>
                      {getUserRoleLabel(USER_ROLE.MERCHANT)}
                    </MenuItem>
                    <MenuItem value={USER_ROLE.SERVICE_PARTNER}>
                      {getUserRoleLabel(USER_ROLE.SERVICE_PARTNER)}
                    </MenuItem>
                    <MenuItem value={USER_ROLE.CUSTOMER}>
                      {getUserRoleLabel(USER_ROLE.CUSTOMER)}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-firstName-field">
                    {"First name"}
                  </InputLabel>
                  <OutlinedInput
                    type="text"
                    name="firstName"
                    label="firstName"
                    disabled={false}
                    // id="as-firstName-field"
                    value={values.firstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.firstName && errors.firstName && (
                    <FormHelperText error id="as-helper-firstName">
                      {errors?.firstName?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-lastName-field">
                    {"Last name"}
                  </InputLabel>
                  <OutlinedInput
                    type="text"
                    name="lastName"
                    label="lastName"
                    disabled={false}
                    // id="as-lastName-field"
                    value={values.lastName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.lastName && errors.lastName && (
                    <FormHelperText error id="as-helper-lastName">
                      {errors?.lastName?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-email-field">
                    {"Email Address"}
                  </InputLabel>
                  <OutlinedInput
                    type="email"
                    name="email"
                    label="Email Address"
                    disabled={false}
                    id="as-email-field"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.email && errors.email && (
                    <FormHelperText error id="as-helper-email">
                      {errors?.email?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-mobile-field" shrink={true}>
                    {"Mobile Number"}
                  </InputLabel>
                  <OutlinedInput
                    type="text"
                    id="as-mobile-field"
                    value={formatNumber(values.phoneNumber)}
                    name="phoneNumber"
                    onBlur={handleBlur}
                    label="Mobile Number"
                    onChange={(e: any) => {
                      if (e.target.value.length <= 10) {
                        setFieldValue(
                          "phoneNumber",
                          formatNumber(e.target.value)
                        );
                      } else {
                        return;
                      }
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Typography sx={formStyles.prefix}>{"+91"}</Typography>
                      </InputAdornment>
                    }
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <FormHelperText error id="as-helper-mobile">
                      {errors.phoneNumber.toString()}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel htmlFor="as-password-field">
                    {"Password"}
                  </InputLabel>
                  <OutlinedInput
                    name="password"
                    label="Password"
                    disabled={false}
                    id="as-password-field"
                    value={values.password}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      // Re-check confirm after password changes (oneOf depends on it)
                      if (values.confirmPassword) {
                        setFieldTouched("confirmPassword", true, false);
                        setTimeout(() => {
                          void validateField("confirmPassword");
                        }, 0);
                      }
                    }}
                    type={showPassword ? "text" : "password"}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Show password"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={formStyles.adornmentButton}
                        >
                          {showPassword ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.password && errors.password && (
                    <FormHelperText error id="as-helper-password">
                      {errors?.password?.toString()}
                    </FormHelperText>
                  )}
                  <FormHelperText>
                    Leave blank to keep the current password.
                  </FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-confirmPassword-field">
                    {"Confirm Password"}
                  </InputLabel>
                  <OutlinedInput
                    name="confirmPassword"
                    label="Confirm Password"
                    disabled={false}
                    id="as-confirmPassword-field"
                    value={values.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type={showConfirmPassword ? "text" : "password"}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Show confirm password"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={formStyles.adornmentButton}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {touched.confirmPassword && errors.confirmPassword && (
                    <FormHelperText error id="as-helper-confirmPassword">
                      {errors?.confirmPassword?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
          </FormSection>

          <FormSection title="Address">
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-addressLine1-field">
                    {"Address Line1"}
                  </InputLabel>
                  <OutlinedInput
                    type="text"
                    name="addressLine1"
                    label="addressLine1"
                    disabled={false}
                    id="as-addressLine1-field"
                    value={values.addressLine1}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.addressLine1 && errors.addressLine1 && (
                    <FormHelperText error>
                      {errors?.addressLine1?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-addressLine2-field">
                    {"Address Line2"}
                  </InputLabel>
                  <OutlinedInput
                    type="text"
                    name="addressLine2"
                    label="addressLine2"
                    disabled={false}
                    id="as-addressLine2-field"
                    value={values.addressLine2}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.addressLine2 && errors.addressLine2 && (
                    <FormHelperText error id="as-helper-addressLine2">
                      {errors?.addressLine2?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-city-field">{"City"}</InputLabel>
                  <OutlinedInput
                    type="text"
                    name="city"
                    label="City"
                    disabled={false}
                    id="as-city-field"
                    value={values.city}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.city && errors.city && (
                    <FormHelperText error id="as-helper-city">
                      {errors?.city?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-state-field">{"State"}</InputLabel>
                  <OutlinedInput
                    type="text"
                    name="state"
                    label="State"
                    disabled={false}
                    id="as-state-field"
                    value={values.state}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {touched.state && errors.state && (
                    <FormHelperText error id="as-helper-cstateity">
                      {errors?.state?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel htmlFor="as-pinCode-field">
                    {"Pin Code"}
                  </InputLabel>
                  <OutlinedInput
                    sx={styles.removeArrow}
                    type="number"
                    name="pincode"
                    label="PinCode"
                    disabled={false}
                    id="as-pinCode-field"
                    value={values.pincode}
                    onBlur={handleBlur}
                    onChange={(e: any) => {
                      if (e.target.value.length <= 6) {
                        setFieldValue("pincode", e.target.value);
                      } else {
                        return;
                      }
                    }}
                  />
                  {touched.pincode && errors.pincode && (
                    <FormHelperText error id="as-helper-pinCode">
                      {errors?.pincode?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
          </FormSection>

          {values.role !== USER_ROLE.CUSTOMER && (
            <FormSection title="Work details">
              {values.role === USER_ROLE.MERCHANT && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-gstNumber-field">
                      {"GST Number"}
                    </InputLabel>
                    <OutlinedInput
                      type="text"
                      name="gstNumber"
                      label="GST Number"
                      disabled={false}
                      id="as-gstNumber-field"
                      value={values.gstNumber}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {touched.gstNumber && errors.gstNumber && (
                      <FormHelperText error id="as-helper-gstNumber">
                        {errors?.gstNumber?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}
              {(values.role === USER_ROLE.COORDINATOR || values.role === USER_ROLE.SERVICE_PARTNER) && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-document-field">
                      {"Document"}
                    </InputLabel>
                    <OutlinedInput
                      sx={formStyles.uploadField}
                      type="text"
                      name="document"
                      label="Document"
                      readOnly
                      id="as-document-field"
                      value={values.document || docLink}
                      onBlur={handleBlur}
                      onClick={onUploadDocumentClick}
                      endAdornment={
                        <InputAdornment position="end">
                          {(values.document || docLink) ? (
                            <IconButton
                              edge="end"
                              aria-label="Preview document"
                              sx={formStyles.adornmentButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDocPreviewOpen(true);
                              }}
                            >
                              <VisibilityOutlined />
                            </IconButton>
                          ) : null}
                          <IconButton
                            edge="end"
                            aria-label="Upload document"
                            sx={formStyles.adornmentButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              onUploadDocumentClick();
                            }}
                          >
                            <UploadFileOutlinedIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <input
                      type="file"
                      ref={docRef}
                      id="docInput"
                      style={{ display: "none" }}
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(e) => onDocumentPicked(e, setFieldValue)}
                    />
                    {touched.document && errors.document && !docLink && (
                      <FormHelperText error id="as-helper-document">
                        {errors?.document?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}
              {(values.role === USER_ROLE.COORDINATOR || values.role === USER_ROLE.SERVICE_PARTNER) && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-salary-field">
                      {"Salary"}
                    </InputLabel>
                    <OutlinedInput
                      sx={styles.removeArrow}
                      startAdornment={
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={formStyles.rupee} />
                        </InputAdornment>
                      }
                      type="number"
                      name="salary"
                      label="Salary"
                      disabled={false}
                      id="as-salary-field"
                      value={values.salary}
                      onBlur={handleBlur}
                      onChange={(e: any) => {
                        if (e.target.value.length <= 5) {
                          setFieldValue("salary", e.target.value);
                        } else {
                          return;
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.code === "KeyE" ||
                          e.code === "Minus" ||
                          e.code === "Space"
                        )
                          e.preventDefault();
                      }}
                    />
                    {touched.salary && errors.salary && (
                      <FormHelperText error id="as-helper-salary">
                        {errors?.salary?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}
              {(values.role === USER_ROLE.COORDINATOR ||
                values.role === USER_ROLE.MERCHANT ||
                values.role === USER_ROLE.SERVICE_PARTNER) && (
                    <FormControl fullWidth >
                      <InputLabel htmlFor="as-dateOfJoining-field" shrink={true}>
                        {"Date Of Joining"}
                      </InputLabel>
                      <OutlinedInput
                        type="date"
                        name="dateOfJoining"
                        label="Date of joining"
                        disabled={false}
                        value={values.dateOfJoining}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        // sx={styles.datePicker}
                        sx={{ input: { cursor: 'pointer' } }}
                      />
                      {touched.dateOfJoining && errors.dateOfJoining && (
                        <FormHelperText error id="as-helper-dateOfJoining">
                          {errors?.dateOfJoining?.toString()}
                        </FormHelperText>
                      )}
                    </FormControl>
                )}
              {values.role === USER_ROLE.MERCHANT && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-registrationNumber-field">
                      {"Registration"}
                    </InputLabel>
                    <OutlinedInput
                      type="text"
                      name="registrationNumber"
                      label="Registration Number"
                      disabled={false}
                      id="as-registrationNumber-field"
                      value={values.registrationNumber}
                      onBlur={handleBlur}
                      onChange={(e: any) => {
                        if (e.target.value.length <= 10) {
                          setFieldValue("registrationNumber", e.target.value);
                        } else {
                          return;
                        }
                      }}
                    />
                    {touched.registrationNumber &&
                      errors.registrationNumber && (
                        <FormHelperText error id="as-helper-registrationNumber">
                          {errors?.registrationNumber?.toString()}
                        </FormHelperText>
                      )}
                  </FormControl>
              )}
              {values.role === USER_ROLE.MERCHANT && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-franchiseAmount-field">
                      {"Franchise Amount"}
                    </InputLabel>
                    <OutlinedInput
                      type="number"
                      name="franchiseAmount"
                      label="Franchise Amount"
                      disabled={false}
                      id="as-franchiseAmount-field"
                      value={values.franchiseAmount}
                      onBlur={handleBlur}
                      onChange={(e: any) => {
                        setFieldValue("franchiseAmount", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.code === "KeyE" ||
                          e.code === "Minus" ||
                          e.code === "Space"
                        )
                          e.preventDefault();
                      }}
                    />
                    {touched.franchiseAmount && errors.franchiseAmount && (
                      <FormHelperText error id="as-helper-franchiseAmount">
                        {errors?.franchiseAmount?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}
              {values.role === USER_ROLE.MERCHANT && (
                  <FormControl fullWidth>
                    <InputLabel htmlFor="as-numberOfStaff-field">
                      {"Number of Service Partner"}
                    </InputLabel>
                    <OutlinedInput
                      type="number"
                      name="numberOfStaff"
                      label="Number of Service Partner"
                      disabled={false}
                      id="as-numberOfStaff-field"
                      value={values.numberOfStaff}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (
                          e.code === "KeyE" ||
                          e.code === "Minus" ||
                          e.code === "Space"
                        )
                          e.preventDefault();
                      }}
                    />
                    {touched.numberOfStaff && errors.numberOfStaff && (
                      <FormHelperText error id="as-helper-numberOfStaff">
                        {errors?.numberOfStaff?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}

              {values.role === USER_ROLE.SERVICE_PARTNER && result?.length > 0 && (
                  <FormControl fullWidth>
                    <UserDopdown
                      userType={USER_TYPE.MERCHANT}
                      isEdit={true}
                      selectedUser={userData.merchant}
                      onSelectUser={(user: UserDetailModal | null) => {
                        const merchantId = user?.id || (user as any)?._id || "";
                        setFieldValue("vendorId", merchantId);
                      }}
                    />
                    {touched.vendorId && errors.vendorId && (
                      <FormHelperText error>
                        {errors?.vendorId?.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
              )}
            </FormSection>
          )}
              {values.role === USER_ROLE.CUSTOMER && (
                <Box sx={formStyles.section}>
                  <Typography component="h2" sx={formStyles.sectionTitle}>
                    Vehicles
                  </Typography>
                  <Box sx={formStyles.grid}>
                    <FormControl fullWidth>
                      <CarCompaniesDropdown
                        key={`car-company-${vehiclePickerKey}`}
                        onSelectCarCompany={(carCompany: any) => {
                          setFieldValue(
                            "vehicleBrandId",
                            carCompany?.id || ""
                          );
                          setFieldValue(
                            "companyName",
                            carCompany?.name || ""
                          );
                          setFieldValue("vehicleId", "");
                        }}
                      />
                      {touched.vehicleBrandId && errors.vehicleBrandId && (
                        <FormHelperText error id="as-helper-vehicleBrandId">
                          {errors?.vehicleBrandId?.toString()}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <FormControl fullWidth>
                      <VehiclesDropdown
                        key={`vehicle-${vehiclePickerKey}`}
                        companyId={values.vehicleBrandId}
                        onSelectVehicle={(vehicle: any) => {
                          setFieldValue(
                            "vehicleId",
                            vehicle?.vehicleId || vehicle?.id || ""
                          );
                          setFieldValue(
                            "vehicleType",
                            vehicle?.vehicleType === 0 || vehicle?.vehicleType
                              ? Number(vehicle.vehicleType)
                              : ""
                          );
                        }}
                      />
                      {touched.vehicleId && errors.vehicleId && (
                        <FormHelperText error id="as-helper-vehicleId">
                          {errors?.vehicleId?.toString()}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Button
                      variant="outlined"
                      sx={formStyles.addVehicle}
                      onClick={() => {
                        if (!values.vehicleBrandId || !values.vehicleId) {
                          SHOW_TOAST(
                            "Please select car company and vehicle"
                          );
                          return;
                        }
                        setLoadingState(true);
                        dispatch(
                          addVichleToUser({
                            data: {
                              vehicleId: values.vehicleId,
                              vehicleBrandId: values.vehicleBrandId,
                              userId: UserId,
                              ...(values.vehicleType !== "" &&
                              values.vehicleType != null
                                ? { vehicleType: Number(values.vehicleType) }
                                : {}),
                            },
                            callback: (result?: any) => {
                              setLoadingState(false);
                              if (!result) return;
                              setFieldValue("vehicleBrandId", "");
                              setFieldValue("companyName", "");
                              setFieldValue("vehicleId", "");
                              setFieldValue("vehicleType", "");
                              setVehiclePickerKey((key) => key + 1);
                              getMyVichles();
                            },
                          })
                        );
                      }}
                    >
                      Add vehicle
                    </Button>
                  </Box>
                  {userVechilesList.length > 0 && (
                    <Box sx={formStyles.vehicleList}>
                      {userVechilesList.map((item: any, i) => {
                        const isBike =
                          String(item.vehicleType) === "1";
                        const vehicleType = isBike ? "Bike" : "Car";
                        const TypeIcon = isBike
                          ? TwoWheelerOutlinedIcon
                          : DirectionsCarOutlinedIcon;

                        return (
                          <Box
                            key={item._id || item.id || i}
                            sx={formStyles.vehicleRow}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={formStyles.vehicleLabel}>
                                Company
                              </Typography>
                              <Typography sx={formStyles.vehicleValue}>
                                {item.companyName || "N/A"}
                              </Typography>
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={formStyles.vehicleLabel}>
                                Vehicle
                              </Typography>
                              <Typography sx={formStyles.vehicleValue}>
                                {item.name || "N/A"}
                              </Typography>
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                ...formStyles.typePill,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <TypeIcon sx={{ fontSize: 16 }} />
                              {vehicleType}
                            </Box>
                            <IconButton
                              aria-label="Remove vehicle"
                              sx={formStyles.removeVehicle}
                              onClick={() => {
                                const deleteId = item._id;
                                if (!deleteId || deleteId === UserId) {
                                  SHOW_TOAST(
                                    "Vehicle id not found in user details"
                                  );
                                  return;
                                }
                                setLoadingState(true);
                                dispatch(
                                  delelteVichleToUser({
                                    data: {
                                      id: deleteId,
                                      userId: UserId,
                                    },
                                    callback: (result?: any) => {
                                      setLoadingState(false);
                                      if (!result) return;
                                      getMyVichles();
                                    },
                                  })
                                );
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={formStyles.footer}>
                <Button
                  type="button"
                  variant="contained"
                  sx={formStyles.submit}
                  disabled={
                    loading ||
                    docUploading ||
                    isSubmitting ||
                    (!profilePhotoChanged && !dirty)
                  }
                  onClick={() => {
                    // Enable on any dirty change (e.g. password). Formik
                    // validates on submit and surfaces field errors.
                    if (profilePhotoChanged || selectedProfileFileRef.current) {
                      handleOnSubmit(values);
                      return;
                    }
                    handleSubmit();
                  }}
                >
                  Update User
                </Button>
              </Box>
            {loading || (loader && <Loader />)}
          </Box>
        )}
      </Formik>
    );
  };
  return (
    <>
      {renderView()}
      <DocumentPreviewModal
        open={docPreviewOpen}
        onClose={() => setDocPreviewOpen(false)}
        url={docLink || userData?.document || ""}
        title="Document preview"
      />
      {loading || docUploading || (loader && <Loader />)}
      {isLoadingState && <Loader />}
    </>
  );
}
